import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PolymerSubtype} from 'constants/MolbioConstants';
import AddPolymersMenu from 'molbio/bulk-assembly/add-polymers/AddPolymersMenu';
import {
  BulkAssemblyMethod,
  FragmentProductionMethod,
  METHOD_TO_SPACER_NAME,
  SpacerSupportingBulkAssemblyType,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {BulkAssemblyLayoutError, getErrorText} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import ClickToEditTextField from 'molbio/bulk-assembly/card/ClickToEditTextField';
import {GibsonBinControls} from 'molbio/bulk-assembly/card/GibsonBinControls';
import {GoldenGateBinControls} from 'molbio/bulk-assembly/card/GoldenGateBinControls';
import {borderStyle} from 'molbio/SharedStyles';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BLUE_LIGHT, GRAY_DARK, GRAY_DARKER, RED, WHITE} from 'ui-library/Colors';
import {DnDDragHandleIcon, DnDDragHandleProps} from 'ui-library/drag-and-drop/DnDDragHandle';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export const BULK_ASSEMBLY_CARD_WIDTH = 192;

export enum BulkAssemblyCardType {
  BIN = 'BIN',
  SPACER = 'SPACER',
  CONSTRUCTS = 'CONSTRUCTS',
}

export type BulkAssemblyConstructsCardError =
  | BulkAssemblyLayoutError.TOO_MANY_BINS
  | BulkAssemblyLayoutError.TOO_MANY_EXISTING_CUTSITES_BINS_FOR_GIBSON;

// Props that apply to all cards.
interface BaseCardProps {
  /** Props to be applied to a drag handle, if one exists on the card */
  dragHandleProps?: DnDDragHandleProps;
  /** The errors associated with the card. */
  errors: Array<BulkAssemblyLayoutError>;
  /** The bulk assembly method type */
  bulkAssemblyMethod: BulkAssemblyMethod;
}

// Props specific to cards which can contain items (i.e. fragments or constructs).
interface ContainableCardProps {
  /** How many items the card contains */
  numContained: number;
}

// Basic props associated with editable cards (i.e. fragment or spacer cards).
interface EditableCardProps {
  /** The id of the slot. */
  cid: string;
  /** Name of card */
  name: string;
  /** A function for the renderer to handle a change in card name */
  onChangeName: (newName: string) => void;
  /** A function for the renderer to handle card deletion. */
  onDelete: () => void;
  /** Whether the assembly is archived and/or finalized and all inputs locked. */
  isReadOnly: boolean;
  /** The 0-indexed position of the card amongst the slots in the assembly. */
  index: number;
  /** The 0-indexed position of the card amongst the slots of the same type (i.e. bins or spacers) in the assembly. */
  cardTypeIndex: number;
  /** The polymer subtypes supported in this Bulk Assembly */
  supportedPolymerTypes: Array<PolymerSubtype>;
}

/** A 'bin' card represents a location within a construct. It can contain one or more fragments */
export interface BulkAssemblyBinProps extends BaseCardProps, EditableCardProps, ContainableCardProps {
  cardType: BulkAssemblyCardType.BIN;
  /** Whether the bin's name should be changeable */
  lockName?: boolean;
  /** Whether the delete button should be hidden. */
  hideDeleteButton: boolean;
  /** What color the bin card's top swatch should be, represented as an HTML color code */
  color: string;
  /** Callback for opening the add fragments search modal */
  openAddPolymersSearchModal: () => void;
  /** Callback for opening the add from worklist modal */
  openAddPolymersWorklistModal: () => void;
  /** Callback to open modal for adding a particular polymer */
  openAddPolymerModal: (polymerId: string) => Promise<void>;
  /** Icon used to represent fragment sequences */
  fragmentIcon: BenchlingIconName;
  /** Method used for producing fragments in this bin */
  fragmentProductionMethod: FragmentProductionMethod;
  /** Callback to change the fragment production method */
  onChangeFragmentProductionMethod: (method: FragmentProductionMethod) => void;
}

/** A 'spacer' card represents a set of residues to be added as an intermediate step in the bulk assembly process. */
export interface BulkAssemblySpacerProps extends BaseCardProps, EditableCardProps {
  cardType: BulkAssemblyCardType.SPACER;
  /** The bulk assembly method type, narrowed to just those that support spacers */
  bulkAssemblyMethod: SpacerSupportingBulkAssemblyType;
  /** The residues of the spacer */
  residues: string;
  /** A function for the renderer to handle a change in spacer residues */
  onChangeResidues: (newResidues: string) => void;
  /** Callback to sanitize residues, which should remove any invalid characters */
  sanitizeResidues: (newResidues: string) => string;
}

/** A 'constructs' card represents the outcome of the assembly process. */
export interface BulkAssemblyConstructsProps extends BaseCardProps, ContainableCardProps {
  cardType: BulkAssemblyCardType.CONSTRUCTS;
  /** A list of the HTML color codes used as bin swatches in the assembly */
  colors: Array<string>;
}

export type BulkAssemblyInputCardProps = BulkAssemblyBinProps | BulkAssemblySpacerProps;

export type BulkAssemblyCardProps = BulkAssemblyInputCardProps | BulkAssemblyConstructsProps;

export function isBinCard(props: {cardType: BulkAssemblyCardType}): props is BulkAssemblyBinProps {
  return props.cardType === BulkAssemblyCardType.BIN;
}

export function isSpacerCard(props: {cardType: BulkAssemblyCardType}): props is BulkAssemblySpacerProps {
  return props.cardType === BulkAssemblyCardType.SPACER;
}

export function isConstructsCard(props: {
  cardType: BulkAssemblyCardType;
}): props is BulkAssemblyConstructsProps {
  return props.cardType === BulkAssemblyCardType.CONSTRUCTS;
}

/**
 * Creates an Aphrodite StyleDeclaration that describes a set of swatches of background color of equal length.
 *
 * Under the hood, this uses a linear-gradient with hard borders between segments. It comes out looking
 * something like:
 *
 * linear-gradient(to-right, blue, blue 33%, red 33%, red 66%, green 66%, green)
 *
 * linear-gradient accepts a direction of progress ('to right') and then a list of starting & ending points
 * that each color will progress across the element. Because we have an arbitrary number of swatches to
 * display, we use a %. The second ('blue') and final ('green') arguments are fixed to the beginning and end
 * of the element.
 *
 * @param colors A list of HTML color codes.
 */
export function getConstructsSwatchesStyles(colors: Array<string>): {constructsSwatches: StyleDeclaration} {
  const step = 100 / colors.length;
  const colorSwatches = colors.map((color, i) => `${color} ${i * step}%, ${color} ${(i + 1) * step}%`);
  const gradient = colorSwatches.join(', ');
  return StyleSheet.create({
    constructsSwatches: {
      background: `linear-gradient(to right, ${gradient})`,
    },
  });
}

function CardErrors({
  errors,
  bulkAssemblyMethod,
}: {
  errors: Array<BulkAssemblyLayoutError>;
  bulkAssemblyMethod: BulkAssemblyMethod;
}): JSX.Element {
  if (errors.length === 0) {
    return <></>;
  }
  const errorText = errors.map((error) => getErrorText(error, bulkAssemblyMethod)).join(' ');
  return (
    <Tooltip text={errorText}>
      <BenchlingIcon icon="warning" altText={errorText} className={css(styles.errorIcon)} />
    </Tooltip>
  );
}

function EditableCardHeader(props: BulkAssemblyInputCardProps & {heading: string}): JSX.Element {
  return (
    <>
      <div className={css(styles.header)}>
        <CardErrors errors={props.errors} bulkAssemblyMethod={props.bulkAssemblyMethod} />
        <span className={css(styles.headerText)}>{`${props.heading} ${props.cardTypeIndex + 1}`}</span>
        <span className={css(styles.headerIcons)}>
          {(isSpacerCard(props) || !props.hideDeleteButton) && !props.isReadOnly && (
            <BenchlingIcon
              icon="delete"
              onClick={() => {
                props.onDelete();
              }}
              className={css(styles.icon, styles.interactiveIcon)}
            />
          )}
          {props.dragHandleProps && !props.isReadOnly && (
            <DnDDragHandleIcon
              className={classNames(
                css(styles.icon, styles.interactiveIcon),
                props.dragHandleProps.className
              )}
              onMouseDown={props.dragHandleProps.onMouseDown}
            />
          )}
        </span>
      </div>
    </>
  );
}

export function ContainableCardFooter(
  props: BulkAssemblyBinProps | BulkAssemblyConstructsProps
): JSX.Element {
  const containedType = isConstructsCard(props) ? 'construct' : 'fragment';
  const fragmentIcon: BenchlingIconName = isConstructsCard(props)
    ? ModelTypeConstants.BULK_ASSEMBLY
    : props.fragmentIcon;

  return (
    <div className={css(styles.footer, styles.row)}>
      <BenchlingIcon icon={fragmentIcon} className={css(styles.sequenceIcon)} />
      <span>{`${props.numContained} ${pluralize(props.numContained, containedType)}`}</span>
      {isBinCard(props) && !props.isReadOnly && (
        <Dropdown menu={<AddPolymersMenu {...props} />} closeOnInsideClick={true} closeOnOutsideClick={true}>
          <AddIcon
            altText="Add new fragment to bin"
            className={css(styles.addFragmentButton)}
            dataTestAttrs={{component: 'BulkAssemblyFragmentBinCard', element: 'addButton'}}
          />
        </Dropdown>
      )}
    </div>
  );
}

function NameField({
  name,
  onChangeName,
  ...otherProps
}: {
  name: string;
  onChangeName: (newName: string) => void;
  editable: boolean;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  return (
    <ClickToEditTextField
      value={name}
      onSaveValue={(newName) => {
        const updatedName = newName.trim();
        // Cancel the operation if the user tries to set the name to empty.
        if (updatedName !== '') {
          onChangeName(updatedName);
        }
      }}
      {...otherProps}
      placeholder="Enter a name"
    />
  );
}

function BinCard(props: BulkAssemblyBinProps): JSX.Element {
  const swatchStyles = StyleSheet.create({
    color: {
      backgroundColor: props.color,
    },
  });

  return (
    <div {...getTestProps({component: 'BulkAssemblyOverviewTab', element: 'fragmentBinCard'})}>
      <div className={css(styles.swatch, swatchStyles.color)} />
      <div className={css(styles.cardContent)}>
        <EditableCardHeader {...props} heading={props.cardType} />
        <div className={css(styles.row)}>
          <NameField
            name={props.name}
            onChangeName={props.onChangeName}
            editable={!(props.lockName || props.isReadOnly)}
            dataTestAttrs={{component: 'BulkAssemblyBinCard', element: 'nameField'}}
          />
          {props.bulkAssemblyMethod === BulkAssemblyMethod.GOLDEN_GATE && (
            <div className={css(styles.row)}>
              <GoldenGateBinControls
                fragmentProductionMethod={props.fragmentProductionMethod}
                onChangeFragmentProductionMethod={props.onChangeFragmentProductionMethod}
                isReadOnly={props.isReadOnly}
              />
            </div>
          )}
          {props.bulkAssemblyMethod === BulkAssemblyMethod.GIBSON && (
            <div className={css(styles.row)}>
              <GibsonBinControls
                fragmentProductionMethod={props.fragmentProductionMethod}
                onChangeFragmentProductionMethod={props.onChangeFragmentProductionMethod}
                isReadOnly={props.isReadOnly}
              />
            </div>
          )}
        </div>
        <ContainableCardFooter {...props} />
      </div>
    </div>
  );
}

function SpacerCard(props: BulkAssemblySpacerProps): JSX.Element {
  const residuesInputPlaceholder =
    props.bulkAssemblyMethod === BulkAssemblyMethod.CONCATENATION &&
    props.supportedPolymerTypes.includes(PolymerSubtype.aa_sequence)
      ? 'Enter residues'
      : 'Enter bases';
  return (
    <div className={css(styles.cardContent)}>
      <EditableCardHeader
        {...props}
        heading={METHOD_TO_SPACER_NAME[props.bulkAssemblyMethod].toUpperCase()}
      />
      <div className={css(styles.row)}>
        <NameField
          name={props.name}
          onChangeName={props.onChangeName}
          editable={!props.isReadOnly}
          dataTestAttrs={{component: 'BulkAssemblySpacerCard', element: 'nameField'}}
        />
      </div>
      <div className={css(styles.row)}>
        <ClickToEditTextField
          value={props.residues}
          onSaveValue={props.onChangeResidues}
          sanitize={props.sanitizeResidues}
          placeholder={residuesInputPlaceholder}
          isArea={true}
          editable={!props.isReadOnly}
          spellCheck={false}
          dataTestAttrs={{component: 'BulkAssemblySpacerCard', element: 'basesField'}}
        />
      </div>
    </div>
  );
}

function ConstructsCard(props: BulkAssemblyConstructsProps): JSX.Element {
  return (
    <>
      <div className={css(styles.swatch, getConstructsSwatchesStyles(props.colors).constructsSwatches)} />
      <div className={css(styles.cardContent)}>
        <div className={css(styles.header)}>
          <CardErrors errors={props.errors} bulkAssemblyMethod={props.bulkAssemblyMethod} />
        </div>
        <div className={css(styles.row)}>
          <div className={css(styles.name)}>Constructs</div>
        </div>
        <ContainableCardFooter {...props} />
      </div>
    </>
  );
}

/**
 * A 'card' view representing a slot in an assembly workflow.
 *
 * @param props Component props; docstrings inline
 * @constructor
 */
export default function BulkAssemblyCard(props: BulkAssemblyCardProps): JSX.Element {
  const useDashedBorder = isSpacerCard(props) ? props.residues.length === 0 : props.numContained === 0;
  const useErrorBorder = props.errors.length > 0;
  const useSwatchCardBorder = isConstructsCard(props) || isBinCard(props);

  return (
    <div
      className={classNames(css(styles.container), {
        [css(styles.errorBorder)]: useErrorBorder,
        [css(styles.dashedBorder)]: useDashedBorder,
        [css(styles.swatchCardBorder)]: useSwatchCardBorder,
      })}
    >
      {isConstructsCard(props) && <ConstructsCard {...props} />}
      {isSpacerCard(props) && <SpacerCard {...props} />}
      {isBinCard(props) && <BinCard {...props} />}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    width: `${BULK_ASSEMBLY_CARD_WIDTH}px`,
    borderRadius: '4px',
    color: GRAY_DARKER,
    border: borderStyle,
    backgroundColor: WHITE,
  },
  cardContent: {
    padding: '8px',
  },
  swatch: {
    height: '4px',
    borderRadius: '4px 4px 0px 0px',
  },
  errorBorder: {
    borderColor: RED,
  },
  dashedBorder: {
    borderStyle: 'dashed',
  },
  swatchCardBorder: {
    borderTop: 'none',
  },
  header: {
    display: 'flex',
  },
  headerText: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  errorIcon: {
    color: RED,
    width: '12px',
    height: '12px',
    marginRight: '8px',
  },
  headerIcons: {
    marginLeft: 'auto',
  },
  icon: {
    color: GRAY_DARK,
    width: '16px',
    height: '16px',
    marginLeft: '8px',
  },
  interactiveIcon: {
    ':hover': {
      color: GRAY_DARKER,
      cursor: 'pointer',
    },
  },
  name: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
  },
  sequenceIcon: {
    marginRight: '8px',
  },
  addFragmentButton: {
    color: BLUE_LIGHT,
    height: '16px',
    width: '16px',
    flex: '0 0 16px',
    marginLeft: '8px',
  },
  row: {
    marginTop: '8px',
  },
});
